import {API} from 'common/api';
import type {OAuthProvider} from './types';

export type OAuthExchangeResult = {
  error?: string;
};

/**
 * Exchanges OAuth callback params for a session. The backend sets the session
 * cookie on success, or returns an `error` ("user not found" when logging in
 * without an account; "account exists" when signing up with one already).
 */
export const oauthExchange = async (
  provider: OAuthProvider,
  params: Record<string, string>,
): Promise<OAuthExchangeResult> => {
  const data = await API.post(`/v1/auth/${provider}`, params);
  return (data ?? {}) as OAuthExchangeResult;
};

/**
 * Encode arbitrary key-value pairs into a URL-safe OAuth state parameter.
 * Use decodeOAuthState in the callback to recover the values.
 */
export function encodeOAuthState(params: Record<string, string>): string {
  return encodeURIComponent(btoa(JSON.stringify(params)));
}

/**
 * Decode an OAuth state parameter produced by encodeOAuthState.
 * Returns null if the state is missing, malformed, or not produced by this function.
 */
export function decodeOAuthState(state: string): Record<string, string> | null {
  try {
    const decoded = JSON.parse(atob(decodeURIComponent(state)));
    if (typeof decoded !== 'object' || decoded === null || Array.isArray(decoded)) return null;
    return decoded as Record<string, string>;
  } catch {
    return null;
  }
}
